import { isObject } from '@mimir/primitive/type-guards/isObject';

export interface TimedMdfUsageRuleIdentifier {
  key: string;
  label: string;
}

export interface TimedMdfUsageRule {
  identifier: TimedMdfUsageRuleIdentifier;
  contexts: UsageRuleTimingContext[];
}

export const restrictedMaterialIcon = 'warning';
export const detectedTextMaterialIcon = 'short_text';

export const dateOffsetTypes = <const>['ingest_time', 'absolute', 'forever'];

export interface UsageRuleDateRangeIngestTime {
  offsetFrom: 'ingest_time';
  untilNumberOfDays: number;
  absoluteDate?: never;
}

export interface UsageRuleDateRangeAbsolute {
  offsetFrom: 'absolute';
  absoluteDate: string; // ISO date string
  untilNumberOfDays?: never;
}

export interface UsageRuleDateRangeForever {
  offsetFrom: 'forever';
  absoluteDate?: never;
  untilNumberOfDays?: never;
}

export type UsageRuleDateRange = UsageRuleDateRangeIngestTime | UsageRuleDateRangeAbsolute | UsageRuleDateRangeForever;

export const warningLevels = <const>['always', 'upon_expiry', 'never'];
export type WarningLevel = typeof warningLevels[number];


export interface UsageRuleTimingContextValidity {
  from: UsageRuleDateRange;
  to: UsageRuleDateRange;
}

export interface UsageRuleTimingContext {
  name: string;
  validity: UsageRuleTimingContextValidity;
  warningLevel: WarningLevel | undefined;
}


export const isInstanceOfUsageRuleTimingContext = (obj: unknown): obj is UsageRuleTimingContext => {
  if (!isObject(obj)) {
    return false;
  }
  const { name, validity, warningLevel } = obj as Partial<UsageRuleTimingContext>;
  return !!(
    name && typeof name === 'string'
    && isInstanceOfUsageRuleDateRange(validity?.from) && isInstanceOfUsageRuleDateRange(validity?.to)
    && ((warningLevel && warningLevels.includes(warningLevel)) || !warningLevel)
  );
};


export const isInstanceOfUsageRuleDateRange = (obj: unknown): obj is UsageRuleDateRange => {
  if (!isObject(obj)) {
    return false;
  }
  const { offsetFrom } = obj as Partial<UsageRuleDateRange>;
  const isValidOffset = offsetFrom && dateOffsetTypes.includes(offsetFrom);
  if (!isValidOffset) {
    return false;
  }

  switch (offsetFrom) {
    case 'ingest_time': {
      const { untilNumberOfDays } = obj as Partial<UsageRuleDateRangeIngestTime>;
      return typeof untilNumberOfDays === 'number' && untilNumberOfDays > -1;
    }
    case 'absolute': {
      const { absoluteDate } = obj as Partial<UsageRuleDateRangeAbsolute>;
      return !!absoluteDate && typeof absoluteDate === 'string';
    }
    case 'forever':
      return true;
    default:
      return false;
  }
};

